import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  PencilSquare,
  Trash3,
  Telephone,
  Envelope,
  Search,
  PlusLg,
  BoxSeam,
  Grid,
  List,
} from "react-bootstrap-icons";
import Drawer from "../../components/Common/Drawer";
import TooltipWrapper from "../../components/Common/TooltipWrapper";
import { useNavigate } from "react-router-dom";
import {
  deleteStoreOwner,
  getStoreOwnerList,
} from "../../services/storeOwnerServices";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import ConfirmModal from "../../components/Common/ConfirmModal";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import DesktopTableSkeleton from "../../components/Common/DesktopTableSkeleton";
import { useTranslation } from "react-i18next";
import { notify } from "../../components/Common/Alerts";
import config from "../../config";

const StoreOwnerList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [params, setParams] = useState({
    limit: config.DATA_LIMIT,
    page: 1,
    search: "",
    is_reload: true,
  });
  const [storeOwnerList, setStoreOwnerList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const currentPage = params.page;
  const totalPages = Math.ceil(totalRecord / params.limit);
  const startIndex = (currentPage - 1) * params.limit;
  const endIndex = startIndex + params.limit;
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchStoreOwnerList = useCallback(() => {
    if (params.page === 1) {
      setLoading(true); // full page loader
    } else {
      setLoadingPage(true); // pagination loader
    }
    getStoreOwnerList(params)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setStoreOwnerList((prev) =>
          params.is_reload ? newData : [...prev, ...newData],
        );

        setTotalRecord(decrypted.total || 0);

        if (newData.length < params.limit) setHasMore(false);
      })
      .finally(() => {
        if (!isMobile) setLoading(false);
        setLoadingPage(false);
        setSkeletonLoading(false);
      });
  }, [params]);

  useEffect(() => {
    fetchStoreOwnerList();
  }, [params]);

  useEffect(() => {
    if (!isMobile) setLoading(false);
    setSkeletonLoading(true);

    // Reset state
    // setStoreOwnerList([]);
    setHasMore(true);

    setParams((prev) => ({
      ...prev,
      page: 1,
      is_reload: true,
    }));
  }, [isMobile]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (isBottom && hasMore && !loadingPage) {
      setLoadingPage(true);

      setParams((prev) => ({
        ...prev,
        page: prev.page + 1,
        is_reload: false,
      }));
    }
  };

  const handleDeleteStoreOwner = (e) => {
    e.preventDefault();
    setBtnDeleteLoading(true);
    deleteStoreOwner({ uuid: deleteId.uuid })
      .then((response) => {
        let decrypt = decryptData(response.data.data);
        notify.success(decrypt.message);
        setStoreOwnerList((prev) =>
          prev.filter((item) => item.uuid !== deleteId.uuid),
        );
        setDeleteShow(false);
      })
      .catch((err) => notify.error(Utils.getErrorMessage(err)))
      .finally(() => setBtnDeleteLoading(false));
  };

  const handleParams = (page) => setParams((prev) => ({ ...prev, page }));

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
      is_reload: true,
    }));
    setShowOffcanvas(false);
  };

  const handleReset = () => {
    setFilters({
      search: "",
    });
    setParams({
      limit: 10,
      page: 1,
      search: "",
      is_reload: true,
    });
    setHasMore(true);
    setLoadingPage(false);
    setShowOffcanvas(false);
  };

  return (
    <>
      <div className="h-full flex flex-col animate-fade-up space-y-4 md:space-y-6">
        <div className="flex justify-between items-center px-2">
          <div>
            <h6 className="text-xl md:text-2xl font-black text-slate-800">
              {t("storeOwner")}
            </h6>
            <p className="text-[10px] md:text-xs text-gray-400 font-black uppercase tracking-widest">
              {totalRecord} {t("partnersOnboarded")}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              className="mobile-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
            </button>

            <button
              className="desktop-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
              <span>{t("filter")}</span>
            </button>

            <div className="hidden md:flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              {" "}
              <button
                onClick={() => setViewMode("grid")}
                disabled={viewMode === "grid"}
                title={t("switchToGridView")}
                className={`p-2 transition flex items-center justify-center ${
                  viewMode === "grid"
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-white text-slate-600 hover:bg-orange-100"
                }`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                disabled={viewMode === "list"}
                title={t("switchToListView")}
                className={`p-2 transition flex items-center justify-center${
                  viewMode === "list"
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-white text-slate-600 hover:bg-orange-100"
                }`}
              >
                <List size={16} />
              </button>
            </div>

            <button
              className="add-btn"
              onClick={() => navigate("/owner-store-add")}
            >
              <PlusLg />
              <span className="hidden md:block">{t("addNew")}</span>
            </button>
          </div>
        </div>
        {viewMode === "grid" ? (
          <>
            <div
              className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 overflow-y-auto pb-4 px-2 md:px-6"
              onScroll={handleScroll}
              style={{ marginBottom: "88px" }}
            >
              {skeletonLoading ? (
                <FlexibleSkeleton
                  count={params.limit}
                  showAvatar
                  showName
                  showRole
                  showContact
                  showButtons
                />
              ) : storeOwnerList?.length > 0 ? (
                storeOwnerList.map((item) => (
                  <div
                    key={item.uuid}
                    className="group bg-white dark:bg-[#0A1221] border border-slate-100 dark:border-white/[0.05] rounded-[24px] p-5 shadow-sm hover:shadow-md md:hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="relative">
                        {item.profile_image ? (
                          <img
                            src={item.profile_image}
                            className="w-14 h-14 rounded-2xl object-cover border-2 border-white dark:border-slate-800 shadow-sm"
                            alt={item.full_name}
                          />
                        ) : (
                          <div className="w-14 h-14 bg-orange-100 dark:bg-brand-orange/10 text-brand-orange rounded-2xl flex items-center justify-center font-black text-lg border-2 border-white dark:border-slate-800 shadow-sm">
                            {item.full_name?.charAt(0).toUpperCase()}
                          </div>
                        )}

                        <div className="absolute bottom-0 right-0 w-4 h-4 bg-brand-orange border-2 border-white dark:border-slate-900 rounded-full"></div>
                      </div>

                      <div>
                        <h4 className="font-black text-slate-800 dark:text-white">
                          {item.full_name}
                        </h4>

                        <span className="text-[10px] bg-orange-50 dark:bg-brand-orange/10 text-brand-orange px-2 py-0.5 rounded-md font-bold uppercase tracking-widest">
                          {item.role_name}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3 py-3 border-y border-slate-50 dark:border-slate-800">
                      <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm font-bold">
                        <Telephone className="text-brand-orange" />{" "}
                        {item.mobile}
                      </div>
                      <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm font-bold">
                        <Envelope className="text-brand-orange" /> {item.email}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-5">
                      <button
                        onClick={() =>
                          navigate(`/owner-store-update/${item.uuid}`)
                        }
                        className="edit-mobile-btn"
                      >
                        <PencilSquare size={16} /> {t("edit")}
                      </button>
                      <button
                        onClick={() => {
                          setDeleteId(item);
                          setDeleteShow(true);
                        }}
                        className="delete-mobile-btn"
                      >
                        <Trash3 size={16} /> {t("delete")}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data">
                  <BoxSeam className="no-data-icon" />
                  <h3 className="no-data-title">{t("noStoreOwnersFound")}</h3>
                  <p className="no-data-text">{t("tryAdjustingFilter")}</p>
                  <button onClick={handleReset} className="no-data-btn">
                    {t("refresh")}
                  </button>
                </div>
              )}
              {loadingPage && (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="hidden md:flex bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800 shadow-sm flex-1 flex-col overflow-hidden transition-colors min-h-0">
            <div className="overflow-auto flex-1 custom-scrollbar">
              <table className="w-full text-left border-separate border-spacing-0">
                {/* <thead className="bg-brand-orange dark:bg-slate-900 sticky top-0 z-10 border-b border-white/5"> */}
                <thead className="table-header">
                  <tr>
                    <th className="table-header-text first:rounded-tl-[40px]">
                      #
                    </th>
                    <th className="table-header-text">{t("name")}</th>
                    <th className="table-header-text">{t("contactDetails")}</th>
                    <th className="table-header-text">{t("mobile")}</th>
                    <th className="table-header-text">{t("role")}</th>
                    <th className="table-header-text text-right last:rounded-tr-[40px]">
                      {t("action")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {loading ? (
                    <DesktopTableSkeleton
                      rows={storeOwnerList?.length || 5}
                      columns={6}
                    />
                  ) : storeOwnerList?.length > 0 ? (
                    storeOwnerList?.map((item, i) => (
                      <tr key={item.uuid} className="border-b">
                        <td className="table-header-body font-bold text-slate-300 dark:text-slate-600">
                          {startIndex + i + 1}
                        </td>
                        <td className="table-header-body">
                          <div className="flex items-center gap-3">
                            {item.profile_image ? (
                              <img
                                src={item.profile_image}
                                className="w-10 h-10 rounded-2xl object-cover border border-slate-100 dark:border-slate-700"
                                alt=""
                              />
                            ) : (
                              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center font-black text-xs">
                                {item.full_name?.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span className="font-bold text-slate-700 dark:text-slate-200">
                              {item.full_name}
                            </span>
                          </div>
                        </td>
                        <td className="table-header-body">
                          <span className="text-slate-400 dark:text-slate-500 text-xs">
                            {item.email}
                          </span>
                        </td>
                        <td className="table-header-body">
                          <span className="text-slate-600 dark:text-slate-300 font-bold">
                            {item.mobile}
                          </span>
                        </td>
                        <td className="table-header-body">
                          <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg text-[10px] font-black uppercase">
                            {item.role_name}
                          </span>
                        </td>
                        <td className="table-header-body">
                          <div className="flex items-center justify-end gap-2">
                            <TooltipWrapper title="Edit" placement="top">
                              <button
                                className="edit-btn"
                                onClick={() =>
                                  navigate(`/owner-store-update/${item.uuid}`)
                                }
                              >
                                <PencilSquare />
                              </button>
                            </TooltipWrapper>
                            <TooltipWrapper title="Edit" placement="top">
                              <button
                                className="delete-btn"
                                onClick={() => {
                                  setDeleteId(item);
                                  setDeleteShow(true);
                                }}
                              >
                                <Trash3 />
                              </button>
                            </TooltipWrapper>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-6 text-slate-400 dark:text-slate-500 font-medium"
                      >
                        {t("noStoreOwnersFound")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {storeOwnerList?.length > 0 && (
              <div className="p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center rounded-b-[40px] transition-colors hidden md:flex">
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-4 md:mb-0">
                  {t("showing")}{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    {startIndex + 1}
                  </span>{" "}
                  {t("to")}{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    {Math.min(endIndex, totalRecord)}
                  </span>{" "}
                  {t("of")} {totalRecord}
                </p>

                <div className="flex items-center gap-2">
                  {/* Prev Button */}
                  <button
                    onClick={() => handleParams(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  {/* Desktop-only Page Numbers */}
                  <div className="hidden md:flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handleParams(page)}
                          className={`w-9 h-9 rounded-xl text-sm font-black transition-all ${
                            currentPage === page
                              ? "bg-brand-orange text-white shadow-lg shadow-orange-200"
                              : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                          }`}
                        >
                          {page}
                        </button>
                      ),
                    )}
                  </div>

                  {/* Mobile-only Page Indicator */}
                  <div className="md:hidden px-4">
                    <span className="text-sm font-black text-brand-orange">
                      {t("page")} {currentPage} {t("of")} {totalPages}
                    </span>
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handleParams(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Drawer
        isOpen={showOffcanvas}
        title={t("filterOwners")}
        onClose={() => setShowOffcanvas(false)}
        footer={
          <div className="flex gap-3 flex-row justify-end w-full">
            <button className="btn-filter-close" onClick={handleReset}>
              {t("reset")}
            </button>

            <button className="btn-submit-orange" onClick={handleSearch}>
              {t("applyFilters")}
            </button>
          </div>
        }
      >
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label className="input-label">{t("searchByNameEmail")}</label>
            <input
              type="text"
              className="input-field"
              placeholder="Ex: John Doe"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
        </div>
      </Drawer>

      <ConfirmModal
        open={deleteShow}
        onClose={() => setDeleteShow(false)}
        onConfirm={handleDeleteStoreOwner}
        loading={btnDeleteLoading}
        titleKey={t("deleteStoreOwner")}
        descriptionKey={t("deleteConfirmation", { name: deleteId?.full_name })}
        confirmText="Yes, Delete"
        icon={<Trash3 size={40} className="text-red-500" />}
      />
    </>
  );
};

export default StoreOwnerList;
